import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HeaderService } from '@services/header.service';
import { combineLatest, Observable, switchMap } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { UserService } from '@services/user/user.service';
import { Building } from '@app/shared/models/building.interface';
import { NavigationService } from '@services/navigation/navigation.service';
import { CheckboxState, FloorplanTag, SelectableWithTags, Tag, TagType } from '@app/shared/models/tag.interface';
import { TagService } from '@services/tag.service';
import { map } from 'rxjs/operators';
import { SelectableNode, SensorNode } from '@app/shared/models/sensor-node';
import { SensorNodeService } from '@services/sensor-node.service';
import { FloorplanService } from '@services/floorplan.service';
import { MatButtonModule } from '@angular/material/button';
import { BuildingAuthorityType } from '@app/shared/models/building-authority-type';
import { SecurityService } from '@services/security.service';
import { TagChangeEvent, TagsComponent } from '@components/tags/tags.component';
import { ConfirmationDialogService } from '@services/confirmation-dialog/confirmation-dialog.service';
import { ConfirmDialogData } from '@components/dialogs/confirm/confirm.component';
import { PanelToggleComponent } from '@components/panel-toggle/panel-toggle.component';
import { MappedNodesCounterComponent } from '@components/mapped-nodes-counter/mapped-nodes-counter.component';
import { MappingService } from '@services/mapping.service';
import { ToastService } from '@services/toast/toast.service';
import { PowerProfileFormComponent } from '@components/power-profile-form/power-profile-form.component';
import { NodeListCardComponent } from '@components/node-list-card/node-list-card.component';
import { FloorplanComponent } from '@components/floorplan/floorplan.component';

@Component({
  selector: 'app-sensor-nodes',
  imports: [
    CommonModule,
    MatButtonModule,
    PanelToggleComponent,
    MappedNodesCounterComponent,
    PowerProfileFormComponent,
    TagsComponent,
    NodeListCardComponent,
    FloorplanComponent
  ],
  templateUrl: './sensor-nodes.component.html',
  styleUrl: './sensor-nodes.component.scss'
})
export class SensorNodesComponent implements OnInit, OnDestroy {
  building: Building;
  floorId: number;
  tags$: Observable<FloorplanTag[]>;
  isLeftColHidden = false;
  isRightColHidden = false;
  hasManageTenantForBuilding = false;
  private refreshNodesAfterMap: string | number | NodeJS.Timeout;
  private activityTimeout: string | number | NodeJS.Timeout;
  private waitingTimeForPostMappingPause = 200;
  private isPollingForMappingUpdates = false;
  private mappingTimeout: string | number | NodeJS.Timeout;
  private readonly UNMAP_NODE_MSG = 'Do you want to unmap selected node(s)?';
  private readonly UNMAP_PN_NOT_ALLOWED = "Can't unmap PN when in SN3 mapping mode";
  private readonly UNMAPPING_SUCCESSFUL = 'Node unmapped successfully';
  private readonly TAG_CREATE_SUCCESSFUL = 'Tag created successfully';
  private readonly TAG_UPDATE_SUCCESSFUL = 'Tag updated successfully';
  private readonly TAG_DELETE_SUCCESSFUL = 'Tag deleted successfully';
  readonly sortByFields = {
    id: 'Id',
    address: 'Address',
    installedAt: 'Time Since Installation'
  };

  constructor(
    private header: HeaderService,
    private route: ActivatedRoute,
    private userService: UserService,
    private navService: NavigationService,
    private tagService: TagService,
    private snService: SensorNodeService,
    private floorplanService: FloorplanService,
    private securityService: SecurityService,
    private dialogService: ConfirmationDialogService,
    private mappingService: MappingService,
    private toastService: ToastService,
    private changeDetectorRef: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.header.showBuildingsMenu();
    this.header.showSiteMenu();
    this.header.showUserMenu();
    this.header.showSessionMenu();
    this.header.showFloorsMenu();
    this.setBuildingIdAndFloorId();
    this.setupMappingMode();
  }

  ngOnDestroy(): void {
    this.mappingService.clearMappingFlags();
  }

  get currentFloorNodes$(): Observable<SensorNode[]> {
    return this.snService.getCurrentFloorNodes$();
  }

  private setBuildingIdAndFloorId(): void {
    this.route.params
      .pipe(
        switchMap((params) => {
          this.floorId = params.floorId;
          this.loadTags(params.buildingId);
          return this.userService.getBuilding(params.buildingId);
        })
      )
      .subscribe((building) => {
        this.navService.initNavigation(window.location.href, building);
        this.building = building;
      });
  }

  private setupMappingMode(): void {
    this.mappingService.setPostMappingActivity(() => {
      if (!this.isPollingForMappingUpdates) {
        this.snService.setFetchedNodesAfterMapping(false);
        this.isPollingForMappingUpdates = true;
        this.pollForMappingUpdates();
      }
    });
  }

  private checkAndUpdateNodesAfterMapping(): void {
    this.snService.updateNodesAfterMapping(this.floorId, this.building.id);
    this.mappingService.setPostActivityInProgress(false);
  }

  private pollForMappingUpdates(maxRetries = 4): void {
    this.mappingTimeout = setTimeout(() => {
      if (this.mappingService.isActivityWaiting) {
        this.mappingService.setPostActivityInProgress(false);
      }
      // the condition is used to prevent the UI from fetching the nodes from backend if a process is in progress
      if (!this.mappingService.isPostActivityInProgress) {
        this.mappingService.setPostActivityInProgress(true);
        this.checkAndUpdateNodesAfterMapping();
      }

      if (this.isPollingForMappingUpdates && maxRetries > 0) {
        this.pollForMappingUpdates(maxRetries - 1);
      } else {
        this.stopPollingForMappingUpdates();
        this.mappingService.setPostActivityInProgress(false);
      }
    }, 5000);
  }

  private stopPollingForMappingUpdates(): void {
    if (this.mappingTimeout) {
      clearTimeout(this.mappingTimeout);
    }
    this.isPollingForMappingUpdates = false;
  }

  nodeClick(node: SelectableNode): void {
    if (this.floorplanService.isSensorNodeMappingModeActive) {
      if (node.properlyMapped) {
        this.unmapMappedNode(node);
      } else {
        this.mapUnmappedNode(node);
      }
    } else {
      this.updateSelection(node);
    }
    this.snService.createNewNodesReference();
  }

  private unmapMappedNode(node: SelectableNode): void {
    if (node.isPassiveNode) {
      this.toastService.error({ message: this.UNMAP_PN_NOT_ALLOWED, dataCy: 'send-error-toast' });
    } else {
      this.dialogService.open(new ConfirmDialogData(this.UNMAP_NODE_MSG)).subscribe((result) => result && unmapNode());

      const unmapNode = () => {
        this.mappingService.unmapGenericNode(node).subscribe(() => {
          this.toastService.success({ message: this.UNMAPPING_SUCCESSFUL, dataCy: 'send-success-toast' });
          if (this.refreshNodesAfterMap) {
            clearTimeout(this.refreshNodesAfterMap);
          }
          this.refreshNodesAfterMap = setTimeout(() => this.snService.fetchNodes(node.floorId, this.building.id), 3000);
        });
      };
    }
  }

  private mapUnmappedNode(node: SelectableNode): void {
    if (
      !node.isNotifying &&
      node.address == null &&
      !this.mappingService.isActivityInProgress &&
      !this.mappingService.isActivityWaiting
    ) {
      this.mappingService.setMappingTried(true);
      node.isNotifying = true;
      this.mappingService.setActivityWaiting(true);
      this.performMappingActivity(node);
    }
  }

  private updateSelection(node: SelectableNode): void {
    if (this.floorplanService.isCumulativeModeActive) {
      this.snService.toggleEntitySelection(node, this.floorplanService.isCumulativeModeActive);
    } else {
      this.snService.selectEntitiesInArea([node], this.floorplanService.isCumulativeModeActive);
    }
    this.snService.updateQueryParams();
    this.snService.createNewNodesReference();
  }

  private performMappingActivity(node: SelectableNode): void {
    this.activityTimeout = setTimeout(() => {
      if (!this.mappingService.isPostActivityInProgress) {
        this.mappingService.doMapping(node.id, this.building.id).subscribe({
          complete: () => {
            node.isNotifying = false;
            this.stopMappingActivity();
            node.hasMappingBeenAttempted = true;
          },
          error: () => {
            node.isNotifying = false;
            this.stopMappingActivity();
          }
        });
        this.mappingService.setActivityWaiting(false);
      } else {
        this.performMappingActivity(node);
      }
    }, this.waitingTimeForPostMappingPause);
  }

  private stopMappingActivity(): void {
    if (this.activityTimeout) {
      clearTimeout(this.activityTimeout);
    }
  }

  toggleLeft(val: boolean): void {
    this.isLeftColHidden = val;
  }

  toggleRight(val: boolean): void {
    this.isRightColHidden = val;
  }

  toggleBoth(val: boolean): void {
    this.isLeftColHidden = val;
    this.isRightColHidden = val;
  }

  loadTags(buildingId: number): void {
    this.securityService
      .isAuthorizedForBuilding(BuildingAuthorityType.MANAGE_TENANT, buildingId)
      .subscribe((result) => {
        this.hasManageTenantForBuilding = result;
        this.changeDetectorRef.markForCheck();
      });
    this.tags$ = combineLatest([
      this.tagService.getTagsForBuildingId(buildingId, true),
      this.snService.getCurrentFloorSelectedEntities$(),
      this.tagService.tagsForFloor$
    ]).pipe(
      map(([_fetchedTags, nodes, tagsForFloor]) => {
        if (nodes.length === 0) {
          tagsForFloor?.forEach((tag) => (tag.checked = CheckboxState.UNCHECKED));
          return tagsForFloor;
        }
        this.tagService.updateTagsCheckedState(tagsForFloor, nodes);
        return tagsForFloor;
      })
    );
    this.changeDetectorRef.markForCheck();
  }

  toggleTag(event: TagChangeEvent): void {
    const { tag, checked } = event;
    const isTagAddOp = checked === CheckboxState.CHECKED;
    const selectedNodes = this.snService.selectedEntities as SelectableWithTags[];

    const nodeIds = [];

    if (!selectedNodes.length || (tag.tagType === TagType.TENANT && !this.hasManageTenantForBuilding)) return;
    selectedNodes.forEach((node) => {
      node.tags = Array.isArray(node.tags) ? node.tags : [];
      nodeIds.push(node.id);
    });
    if (isTagAddOp) {
      this.snService.tagNodes({ tagIds: [tag.id], nodeIds }).subscribe({
        error: (err) => {
          this.snService.currentFloorNodes.forEach((node: SelectableNode) => {
            if (nodeIds.includes(node.id)) {
              node.tags = Array.isArray(node.tags) ? node.tags : [];
              const tagsToKeep = [];
              node.tags.forEach((nodeTag: Tag) => {
                if (nodeTag.id !== tag.id) {
                  tagsToKeep.push(nodeTag);
                }
              });
              node.tags = tagsToKeep;
            }
          });
          this.snService.createNewNodesReference();
          this.toastService.error({ message: err.error?.message, dataCy: 'tag-add-node-failed-toast' });
        }
      });
    } else {
      this.snService.unTagNodes({ tagIds: [tag.id], nodeIds }).subscribe();
    }
    selectedNodes.forEach((node: SelectableWithTags) => {
      if (isTagAddOp) {
        node.tags = [...node.tags, tag];
      } else {
        const tagsRemoved = node.tags.filter((t) => t.id !== tag.id);
        node.tags = [...tagsRemoved];
      }
    });
    this.snService.createNewNodesReference();
  }

  tagCreate(tag: FloorplanTag): void {
    this.tagService.createTag(tag).subscribe({
      next: () => {
        this.loadTags(tag.buildingId);
        this.toastService.success({ message: this.TAG_CREATE_SUCCESSFUL, dataCy: 'tag-create-success-toast' });
      },
      error: (err) => {
        this.toastService.error({ message: err.error?.message, dataCy: 'tag-create-failed-toast' });
      }
    });
  }

  tagUpdate(tag: FloorplanTag): void {
    this.tagService.updateTag(tag.id, tag).subscribe({
      next: () => {
        this.loadTags(tag.buildingId);
        this.snService.fetchNodes(this.floorId, this.building.id);
        this.toastService.success({ message: this.TAG_UPDATE_SUCCESSFUL, dataCy: 'tag-update-success-toast' });
      },
      error: (err) => {
        this.toastService.error({ message: err.error?.message, dataCy: 'tag-update-failed-toast' });
      }
    });
  }

  deleteTag(tag: FloorplanTag): void {
    const data = new ConfirmDialogData(`Are you sure you want to remove the tag "${tag.name}"?`, 'Delete Tag');
    this.dialogService.open(data).subscribe((result) => {
      if (result) {
        this.tagService.deleteTag(tag.id).subscribe({
          next: () => {
            this.loadTags(tag.buildingId);
            this.snService.fetchNodes(this.floorId, this.building.id);
            this.toastService.success({ message: this.TAG_DELETE_SUCCESSFUL, dataCy: 'tag-update-success-toast' });
          },
          error: (err) => {
            this.toastService.error({ message: err.error?.message, dataCy: 'tag-delete-failed-toast' });
          }
        });
      }
    });
  }

  get floorPanelClass(): string {
    if (this.isLeftColHidden && this.isRightColHidden) {
      return 'col-span-12';
    } else if (this.isLeftColHidden) {
      return 'col-span-10';
    } else if (this.isRightColHidden) {
      return 'col-span-7';
    } else {
      return 'col-span-6';
    }
  }

  get nodeListPanelClass(): string {
    if (this.isLeftColHidden) {
      return 'hidden';
    } else if (this.isRightColHidden) {
      return 'col-span-5';
    } else {
      return 'col-span-4';
    }
  }
}
